import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Save, Trash2, Wand2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, eur, itemImage, fileUrl, CATEGORIES, outUrl } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import Avatar3D, { colorsFromItems } from "@/components/Avatar3D";
import { Page } from "@/components/Page";

const PRESETS = ["Office Casual", "Hochzeitsgast Sommer", "Date Night", "Festival", "Sonntagsspaziergang"];
const key = (it) => `${it.source}:${it.id}`;

function Slot({ cat, item, onRemove }) {
  return (
    <div className="flex items-center gap-4 rounded-xl border border-white/[0.08] bg-[#0A0A0B] p-3" data-testid={`outfit-slot-${cat}`}>
      <div className="h-16 w-14 shrink-0 overflow-hidden rounded-lg bg-[#18181C]">
        {item && <img src={itemImage(item)} alt="" className="h-full w-full object-cover" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-widest text-neutral-500">{cat}</p>
        <p className="truncate text-sm">{item ? item.name : <span className="text-neutral-600">leer</span>}</p>
        {item && <p className="text-xs text-neutral-500">{item.source === "wardrobe" ? "Mein Kleiderschrank" : `${item.brand} · ${eur(item.price)}`}</p>}
      </div>
      {item && item.source === "product" && <a href={outUrl(item.id)} target="_blank" rel="noopener noreferrer sponsored" className="text-xs text-[#D4AF37] hover:underline" data-testid={`slot-shop-${cat}`}>Shop</a>}
      {item && <button onClick={onRemove} className="text-neutral-500 hover:text-white" data-testid={`slot-remove-${cat}`}><X className="h-4 w-4" /></button>}
    </div>
  );
}

export default function Builder() {
  const [products, setProducts] = useState([]);
  const [wardrobe, setWardrobe] = useState([]);
  const [avatar, setAvatar] = useState(null);
  const [tab, setTab] = useState("product");
  const [cat, setCat] = useState("");
  const [picked, setPicked] = useState({});
  const [occasion, setOccasion] = useState("");
  const [aiNote, setAiNote] = useState(null);
  const [name, setName] = useState("");
  const [saved, setSaved] = useState([]);
  const [busy, setBusy] = useState({});
  const [tryImg, setTryImg] = useState(null);

  const load = () => api.get("/outfits").then(({ data }) => setSaved(data));
  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data.map((p) => ({ ...p, source: "product" }))));
    api.get("/wardrobe").then(({ data }) => setWardrobe(data.map((w) => ({ ...w, source: "wardrobe" }))));
    api.get("/avatar").then(({ data }) => setAvatar(data));
    load();
  }, []);

  const items = Object.values(picked);
  const total = items.filter((i) => i.source === "product").reduce((s, i) => s + i.price, 0);
  const list = useMemo(() => (tab === "product" ? products : wardrobe).filter((i) => !cat || i.category === cat), [tab, products, wardrobe, cat]);

  const toggle = (it) => {
    const next = { ...picked };
    if (next[it.category] && key(next[it.category]) === key(it)) delete next[it.category];
    else {
      next[it.category] = it;
      if (it.category === "Kleid") { delete next.Oberteil; delete next.Hose; }
      if (["Oberteil", "Hose"].includes(it.category)) delete next.Kleid;
    }
    setPicked(next);
  };

  const suggest = async (occ) => {
    const o = occ || occasion;
    if (!o.trim()) return toast.error("Beschreibe kurz den Anlass");
    setOccasion(o);
    setBusy({ ai: true });
    try {
      const { data } = await api.post("/outfits/suggest", { occasion: o, use_wardrobe: true }, { timeout: 120000 });
      const next = {};
      data.resolved.forEach((r) => { next[r.category] = r; });
      setPicked(next);
      setAiNote({ title: data.title, description: data.description });
      setName(data.title || "");
      toast.success("Der KI-Stylist hat ein Outfit für dich");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy({});
  };

  const save = async () => {
    if (!items.length) return toast.error("Wähle mindestens ein Teil");
    try {
      await api.post("/outfits", { name: name || "Mein Outfit", occasion, items: items.map((i) => ({ source: i.source, id: i.id })), note: aiNote?.description || "" });
      toast.success("Outfit gespeichert"); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const tryon = async () => {
    if (!items.length) return toast.error("Wähle mindestens ein Teil");
    setBusy({ try: true });
    try {
      const { data } = await api.post("/tryon", { items: items.map((i) => ({ source: i.source, id: i.id })) }, { timeout: 180000 });
      setTryImg(fileUrl(data.image_path));
    } catch (e) { toast.error(errMsg(e)); }
    setBusy({});
  };

  const loadOutfit = (o) => {
    const next = {};
    o.resolved.forEach((r) => { next[r.category] = r; });
    setPicked(next); setName(o.name); setAiNote(o.note ? { title: o.name, description: o.note } : null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const slots = picked.Kleid ? ["Kleid", "Jacke", "Schuhe", "Accessoire"] : ["Oberteil", "Hose", "Jacke", "Schuhe", "Accessoire"];

  return (
    <Page over="Outfit Builder" title="Stelle deinen Look zusammen" sub="Mixe Teile verschiedener Marken mit deinen eigenen Klamotten – oder lass dir vom KI-Stylist ein Outfit vorschlagen." testid="builder-page">
      <div className="surface mb-8 p-6">
        <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#D4AF37]" /><p className="label-over">KI-Outfit-Vorschlag</p></div>
        <div className="mt-4 flex flex-col gap-3 md:flex-row">
          <input value={occasion} onChange={(e) => setOccasion(e.target.value)} onKeyDown={(e) => e.key === "Enter" && suggest()} placeholder="Wofür brauchst du ein Outfit? z.B. Vorstellungsgespräch im Herbst" className="h-12 flex-1 rounded-full border border-white/10 bg-[#0A0A0B] px-5 text-sm outline-none focus:border-[#D4AF37]/60" data-testid="ai-occasion-input" />
          <button onClick={() => suggest()} disabled={busy.ai} className="btn-cream" data-testid="ai-suggest-button"><Wand2 className={`h-4 w-4 ${busy.ai ? "animate-spin" : ""}`} />{busy.ai ? "Stylist denkt nach…" : "Outfit vorschlagen"}</button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {PRESETS.map((p) => <button key={p} onClick={() => suggest(p)} disabled={busy.ai} className="rounded-full border border-white/10 px-3 py-1.5 text-xs text-neutral-400 transition-colors hover:border-[#D4AF37]/50 hover:text-white" data-testid={`ai-preset-${p.replace(/\s+/g, "-").toLowerCase()}`}>{p}</button>)}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        <aside className="space-y-4 lg:col-span-4">
          <div className="surface h-72 overflow-hidden">{avatar && <Avatar3D measurements={avatar} colors={colorsFromItems(items)} photoUrl={fileUrl(avatar.photo_path)} />}</div>
          <div className="surface space-y-3 p-5" data-testid="outfit-board">
            {aiNote && <div className="rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-4" data-testid="ai-suggestion-note"><p className="font-serif text-lg">{aiNote.title}</p><p className="mt-1 text-xs leading-relaxed text-neutral-400">{aiNote.description}</p></div>}
            {slots.map((c) => <Slot key={c} cat={c} item={picked[c]} onRemove={() => toggle(picked[c])} />)}
            <div className="flex justify-between border-t border-white/10 pt-3 text-sm"><span className="text-neutral-400">Neue Teile gesamt</span><span className="font-semibold" data-testid="outfit-total-price">{eur(total)}</span></div>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name des Outfits" className="h-11 w-full rounded-xl border border-white/10 bg-[#0A0A0B] px-4 text-sm outline-none" data-testid="outfit-name-input" />
            <div className="grid grid-cols-2 gap-2">
              <button onClick={save} className="btn-cream !px-3" data-testid="outfit-save-button"><Save className="h-4 w-4" /> Speichern</button>
              <button onClick={tryon} disabled={busy.try} className="btn-ghost !px-3" data-testid="outfit-tryon-button"><Wand2 className={`h-4 w-4 ${busy.try ? "animate-spin" : ""}`} />{busy.try ? "Erstellt…" : "KI-Anprobe"}</button>
            </div>
          </div>
        </aside>

        <div className="lg:col-span-8">
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <div className="flex rounded-full border border-white/10 p-1">
              {[["product", "Partner-Marken"], ["wardrobe", "Mein Kleiderschrank"]].map(([t, l]) => (
                <button key={t} onClick={() => setTab(t)} className={`rounded-full px-4 py-2 text-sm transition-colors ${tab === t ? "bg-[#FAF7F0] font-semibold text-black" : "text-neutral-400"}`} data-testid={`builder-tab-${t}`}>{l}</button>
              ))}
            </div>
            <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-10 rounded-full border border-white/10 bg-[#121215] px-4 text-sm" data-testid="builder-category-filter">
              <option value="">Alle Kategorien</option>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          {list.length === 0 ? (
            <p className="surface p-10 text-center text-sm text-neutral-500" data-testid="builder-empty">{tab === "wardrobe" ? "Dein Kleiderschrank ist noch leer – füge im Bereich „Kleiderschrank“ deine Teile hinzu." : "Keine Teile in dieser Kategorie."}</p>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {list.map((p) => <ProductCard key={key(p)} p={p} onAdd={toggle} selected={picked[p.category] && key(picked[p.category]) === key(p)} />)}
            </div>
          )}
        </div>
      </div>

      <section className="mt-16">
        <p className="label-over">Gespeicherte Outfits</p>
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="saved-outfits">
          {saved.map((o) => (
            <div key={o.id} className="card-lux p-5" data-testid={`saved-outfit-${o.id}`}>
              <div className="flex -space-x-3">{o.resolved.slice(0, 5).map((r) => <img key={key(r)} src={itemImage(r)} alt="" className="h-16 w-14 rounded-lg border-2 border-[#18181C] object-cover" />)}</div>
              <p className="mt-4 font-serif text-xl">{o.name}</p>
              {o.occasion && <p className="text-xs text-neutral-500">{o.occasion}</p>}
              <div className="mt-4 flex gap-2">
                <button onClick={() => loadOutfit(o)} className="btn-ghost !px-4 !py-2 text-xs" data-testid={`load-outfit-${o.id}`}>Bearbeiten</button>
                <button onClick={async () => { await api.delete(`/outfits/${o.id}`); load(); }} className="btn-ghost !px-3 !py-2" data-testid={`delete-outfit-${o.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
          {saved.length === 0 && <p className="text-sm text-neutral-500">Noch keine Outfits gespeichert.</p>}
        </div>
      </section>

      <Dialog open={!!tryImg} onOpenChange={() => setTryImg(null)}>
        <DialogContent className="max-w-lg border-white/10 bg-[#121215]" data-testid="tryon-result-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Deine KI-Anprobe</DialogTitle></DialogHeader>
          {tryImg && <img src={tryImg} alt="KI-Anprobe" className="w-full rounded-xl" data-testid="tryon-result-image" />}
        </DialogContent>
      </Dialog>
    </Page>
  );
}
